import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Copy, Search, Check, AlertCircle, ArrowRight } from 'lucide-react';
import { usePIM } from '../../context/PIMContext';
import { useToast } from '../../context/ToastContext';

// --- Sub-component to handle form state and life-cycle ---
const CopyProductForm = ({ sourceProduct, activeCharge, onClose }) => {
    const { copyProduct, loadedProducts } = usePIM();
    const { toast } = useToast();

    const availableTabs = [
        { id: 'general', label: 'General Info (Model, EAN, etc.)', mode: 'new_only' },
        { id: 'properties', label: 'Properties', mode: 'both' },
        { id: 'languages', label: 'Languages', mode: 'both' },
        { id: 'packaging', label: 'Packaging', mode: 'both' },
        { id: 'pictures', label: 'Pictures', mode: 'both' },
        { id: 'documents', label: 'Documents & Software', mode: 'both' },
        { id: 'accessories', label: 'Accessories', mode: 'both' },
        { id: 'tasks', label: 'Tasks', mode: 'new_only' },
        { id: 'address', label: 'Address Assignments', mode: 'both' }
    ];

    // State Initialization directly from props
    const [copyMode, setCopyMode] = useState('new'); // 'new' | 'existing'
    const [targetSku, setTargetSku] = useState(sourceProduct ? `${sourceProduct.sku} (copy)` : '');
    const [targetProduct, setTargetProduct] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const filteredProducts = React.useMemo(() => {
        if (copyMode === 'existing' && searchQuery) {
            const lowerQuery = searchQuery.toLowerCase();
            return loadedProducts.filter(p =>
            (p.sku.toLowerCase().includes(lowerQuery) ||
                p.model_name.toLowerCase().includes(lowerQuery))
            ).slice(0, 5);
        }
        return [];
    }, [searchQuery, loadedProducts, copyMode]);

    // Charge Selection State
    const [sourceCharges, setSourceCharges] = useState([]);
    const [selectedSourceChargeId, setSelectedSourceChargeId] = useState('');

    const [targetCharges, setTargetCharges] = useState([]);
    const [selectedTargetChargeId, setSelectedTargetChargeId] = useState('new'); // 'new' or ID

    const [selectedTabs, setSelectedTabs] = useState(availableTabs.map(t => t.id));

    // Initial Fetch for Source Charges
    useEffect(() => {
        if (!sourceProduct) return;

        const fetchSourceCharges = async () => {
            try {
                const res = await fetch(`/api/charges?sku=${sourceProduct.sku}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (res.ok) {
                    const charges = await res.json();
                    setSourceCharges(charges);
                    // Default selection
                    if (activeCharge) {
                        setSelectedSourceChargeId(activeCharge.id);
                    } else if (charges.length > 0) {
                        setSelectedSourceChargeId(charges[0].id);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch charges", err);
            }
        };
        fetchSourceCharges();
    }, [sourceProduct, activeCharge]);


    // Fetch Target Charges when Target Product Changes
    useEffect(() => {
        if (targetProduct) {
            const fetchTargetCharges = async () => {
                try {
                    const res = await fetch(`/api/charges?sku=${targetProduct.sku}`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (res.ok) {
                        setTargetCharges(await res.json());
                        setSelectedTargetChargeId('new');
                    }
                } catch (err) {
                    console.error("Failed to fetch target charges", err);
                }
            };
            fetchTargetCharges();
        }
    }, [targetProduct]);

    const handleTabToggle = (tabId) => {
        setSelectedTabs(prev => {
            if (prev.includes(tabId)) {
                return prev.filter(t => t !== tabId);
            } else {
                return [...prev, tabId];
            }
        });
    };

    const handleCopy = async () => {
        if (copyMode === 'new' && !targetSku) {
            toast.error("Please enter a SKU for the new product.");
            return;
        }
        if (copyMode === 'existing' && !targetProduct) {
            toast.error("Please select a target product.");
            return;
        }
        if (!selectedSourceChargeId) {
            toast.error("Please select a source charge.");
            return;
        }

        const options = {
            targetMode: copyMode,
            targetSku: copyMode === 'new' ? targetSku : null,
            targetProductId: copyMode === 'existing' ? targetProduct.id : null,
            sourceChargeId: selectedSourceChargeId,
            targetChargeId: (copyMode === 'existing' && selectedTargetChargeId !== 'new') ? selectedTargetChargeId : null,
            selectedTabs: selectedTabs.filter(t => {
                if (copyMode === 'existing' && t === 'general') return false;
                return true;
            })
        };

        const success = await copyProduct(options);
        if (success) {
            onClose();
        }
    };

    const selectTargetProduct = (product) => {
        setTargetProduct(product);
        setTargetCharges([]); // Clear previous charges
        setSelectedTargetChargeId('new');
        setSearchQuery(`${product.sku} - ${product.model_name}`);
    };

    return (
        <div className="space-y-6">
            {/* Mode Selection */}
            <div className="flex gap-4 p-1 bg-stone-800 rounded-lg">
                <button
                    onClick={() => setCopyMode('new')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${copyMode === 'new'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-stone-400 hover:text-stone-200 hover:bg-stone-700'
                        }`}
                >
                    Create New Product
                </button>
                <button
                    onClick={() => setCopyMode('existing')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${copyMode === 'existing'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-stone-400 hover:text-stone-200 hover:bg-stone-700'
                        }`}
                >
                    Add to Existing Product
                </button>
            </div>

            <div className="grid grid-cols-2 gap-6">
                {/* Source Configuration */}
                <div className="space-y-4">
                    <h4 className="text-sm font-bold text-stone-300 uppercase tracking-wider">Source</h4>
                    <div className="p-3 bg-stone-800/50 rounded border border-stone-700">
                        <div className="text-sm text-stone-200 font-medium mb-1">{sourceProduct?.sku}</div>
                        <div className="text-xs text-stone-500 mb-3">{sourceProduct?.model_name}</div>

                        <label className="text-xs font-medium text-stone-400 block mb-1">Source Charge</label>
                        <select
                            value={selectedSourceChargeId}
                            onChange={(e) => setSelectedSourceChargeId(e.target.value)}
                            className="w-full bg-stone-900 border border-stone-700 rounded px-2 py-1.5 text-sm text-stone-200 focus:outline-none focus:border-blue-500"
                        >
                            {sourceCharges.map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.batch_number} ({c.status})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Target Configuration */}
                <div className="space-y-4">
                    <h4 className="text-sm font-bold text-stone-300 uppercase tracking-wider">Target</h4>
                    <div className="bg-stone-800/50 p-4 rounded-lg border border-stone-700 h-full">
                        {copyMode === 'new' ? (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-stone-400">New Product SKU</label>
                                <input
                                    type="text"
                                    value={targetSku}
                                    onChange={(e) => setTargetSku(e.target.value)}
                                    className="w-full bg-stone-900 border border-stone-700 rounded px-3 py-2 text-stone-200 focus:outline-none focus:border-blue-500"
                                    placeholder="Enter SKU"
                                />
                                <div className="text-xs text-stone-500 mt-2">
                                    A new default charge will be created.
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4 relative">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-stone-400">Search Target Product</label>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-2.5 text-stone-500" size={16} />
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => {
                                                setSearchQuery(e.target.value);
                                                setTargetProduct(null); // Reset selection on type
                                            }}
                                            className="w-full bg-stone-900 border border-stone-700 rounded pl-9 pr-3 py-2 text-stone-200 focus:outline-none focus:border-blue-500"
                                            placeholder="Search by SKU or Model..."
                                        />
                                        {targetProduct && (
                                            <div className="absolute right-3 top-2.5 text-green-500">
                                                <Check size={16} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Dropdown Results */}
                                    {filteredProducts.length > 0 && (
                                        <div className="absolute z-10 w-full bg-stone-800 border border-stone-700 rounded-md shadow-xl mt-1 max-h-48 overflow-y-auto">
                                            {filteredProducts.map(p => (
                                                <div
                                                    key={p.id}
                                                    onClick={() => selectTargetProduct(p)}
                                                    className="px-4 py-2 hover:bg-stone-700 cursor-pointer border-b border-stone-700/50 last:border-0"
                                                >
                                                    <div className="text-sm text-stone-200 font-medium">{p.sku}</div>
                                                    <div className="text-xs text-stone-500">{p.model_name}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {targetProduct && (
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-stone-400">Target Charge</label>
                                        <select
                                            value={selectedTargetChargeId}
                                            onChange={(e) => setSelectedTargetChargeId(e.target.value)}
                                            className="w-full bg-stone-900 border border-stone-700 rounded px-3 py-2 text-stone-200 focus:outline-none focus:border-blue-500"
                                        >
                                            <option value="new">+ Create New Charge</option>
                                            {targetCharges.map(c => (
                                                <option key={c.id} value={c.id}>
                                                    {c.batch_number} ({c.status})
                                                </option>
                                            ))}
                                        </select>

                                        {selectedTargetChargeId !== 'new' && (
                                            <div className="flex items-start gap-2 mt-2 text-xs text-amber-400 bg-amber-900/20 p-2 rounded">
                                                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                                                <p>Data will be <strong>appended</strong> to the selected charge.</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Data Selection */}
            <div>
                <h4 className="text-sm font-bold text-stone-300 mb-3 uppercase tracking-wider">Data to Copy</h4>
                <div className="grid grid-cols-2 gap-3">
                    {availableTabs.map(tab => {
                        const isDisabled = copyMode === 'existing' && tab.mode === 'new_only';
                        const isChecked = !isDisabled && selectedTabs.includes(tab.id);

                        return (
                            <label
                                key={tab.id}
                                className={`flex items-center gap-3 p-3 rounded border transition-colors cursor-pointer ${isDisabled
                                    ? 'bg-stone-900/50 border-stone-800 text-stone-600 cursor-not-allowed'
                                    : isChecked
                                        ? 'bg-blue-900/20 border-blue-800/50 text-stone-200'
                                        : 'bg-stone-800 border-stone-700 text-stone-400 hover:border-stone-600'
                                    }`}
                            >
                                <div className={`w-4 h-4 rounded border flex items-center justify-center ${isDisabled
                                    ? 'border-stone-700 bg-stone-800'
                                    : isChecked
                                        ? 'bg-blue-600 border-blue-600'
                                        : 'border-stone-500'
                                    }`}>
                                    {isChecked && <Check size={10} className="text-white" />}
                                </div>
                                <input
                                    type="checkbox"
                                    className="hidden"
                                    checked={isChecked}
                                    disabled={isDisabled}
                                    onChange={() => handleTabToggle(tab.id)}
                                />
                                <span className="text-sm">{tab.label}</span>
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-stone-700">
                <button
                    onClick={onClose}
                    className="px-4 py-2 rounded text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={handleCopy}
                    className="px-6 py-2 rounded bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-lg shadow-blue-900/20 transition-all flex items-center gap-2"
                >
                    <Copy size={16} />
                    {copyMode === 'new' ? 'Create Copy' : 'Copy Data'}
                </button>
            </div>
        </div>
    );
};

export const CopyProductModal = ({ isOpen, onClose, sourceProduct, activeCharge }) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Copy Product Data">
            {isOpen && sourceProduct && (
                <CopyProductForm
                    key={`${sourceProduct.sku}-${isOpen}`} // Force remount on open or product change
                    sourceProduct={sourceProduct}
                    activeCharge={activeCharge}
                    onClose={onClose}
                />
            )}
        </Modal>
    );
};
